import React, { SetStateAction } from 'react'
import Highcharts from 'highcharts2/highstock'
import moment from 'moment'
import { DefaultTheme } from 'styled-components'
import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'
import { ErrorNotification, StaticDataType, STATIC_DATA_STATES } from '../chart-components'
import { STATIC_DATA_ENTITIES } from '../../context/utils'
import { calculateLinearInterpolation } from '../../utils/parse'
import { TimeSpan, xRangeEvent, RawRigTableEvent, RigTableSeries } from '../../utils/type'
import locale from 'live/init/global-locale'

export const getRigTableChartOptions = (
    chart: { timeSpan: TimeSpan; height: number },
    series: RigTableSeries,
    theme: DefaultTheme,
    openModal: (rawEvent: RawRigTableEvent) => void,
    tryOpenModal: (
        event: xRangeEvent,
        entity: STATIC_DATA_ENTITIES
    ) => { canOpen: boolean; parentId: number; hasPermissionToOpenModal: boolean },
    setAddEventButtonVisible: (x: any, y: string) => void,
    setTooltiPosition: (x: { left?: number; top?: number }) => void,
    setTooltipMessage: React.Dispatch<SetStateAction<any>>,
    checkBlockedTrackBeforeAction: (event: any, onNotBlocked: () => void) => void
): Highcharts.Options => ({
    chart: {
        type: 'xrange',
        animation: false,
        backgroundColor: 'transparent',
        margin: 0
    },
    plotOptions: {
        xrange: {
            animation: false,
            cursor: 'pointer',
            borderColor: 'var(--gray-0)',
            borderWidth: 3,
            borderRadius: 1,
            pointPadding: 0.2,
            groupPadding: 0,
            pointWidth: 1000,
            grouping: false,
            turboThreshold: 0,
            minPointLength: 1,
            zIndex: 100,
            states: {
                inactive: {
                    enabled: false
                }
            },
            events: {
                click: event => {
                    checkBlockedTrackBeforeAction(event.point, () => {
                        if (event.point.rawEvent.state === STATIC_DATA_STATES.EMPTY) {
                            const { canOpen, parentId, hasPermissionToOpenModal } = tryOpenModal(
                                event.point,
                                STATIC_DATA_ENTITIES.RIG_TABLE
                            )
                            if (!canOpen)
                                return globalNotification.error(
                                    <ErrorNotification showMessageAboutStartAnEvent={hasPermissionToOpenModal} />,
                                    30000
                                )
                            openModal({ ...event.point.rawEvent, intervention_id: parentId } as RawRigTableEvent)
                        }
                        if (event.point.rawEvent.state === STATIC_DATA_STATES.EMPTY_DISABLED) {
                            globalNotification.error(<ErrorNotification showMessageAboutStartAnEvent={true} />, 30000)
                        }
                    })
                }
            }
        },
        series: {
            point: {
                events: {
                    mouseOver: event => {
                        event?.target?.graphic?.element?.addEventListener('mousemove', e => {
                            let startDate = chart.timeSpan.begin / 1000
                            let endDate = chart.timeSpan.end / 1000
                            let pointerX = e?.layerX
                            let parentStart = event?.target?.parentStart
                            let trackWidth = event?.target?.graphic?.parentGroup?.height
                            const localDate = locale === 'en_us' ? 'MM/DD/YY' : 'DD/MM/YY'

                            const pointerMoment = moment.unix(
                                calculateLinearInterpolation(pointerX, 0, startDate, trackWidth, endDate)
                            )

                            setTooltiPosition({ left: e.clientX, top: e.clientY })
                            setTooltipMessage({
                                date: pointerMoment.format(localDate),
                                hour: pointerMoment.format('HH:mm:ss'),
                                text: `${i18n('Click to add')} ${i18n(StaticDataType.RIG_TABLE)}`,
                                fatherType: i18n(StaticDataType.INTERVENTION),
                                parentStart: parentStart
                                    ? moment.unix(parentStart / 1000).format(`${localDate} hh:mm`)
                                    : ''
                            })
                        })
                        setAddEventButtonVisible(event.target, 'rig_table')
                    },
                    mouseOut: _ => {
                        setTooltipMessage(null)
                    }
                }
            },
            states: {
                hover: {
                    enabled: false
                }
            }
        }
    },
    series: [series],
    xAxis: {
        visible: false,
        min: chart.timeSpan.begin,
        max: chart.timeSpan.end
    },
    yAxis: {
        visible: false
    },
    time: {
        timezoneOffset: new Date().getTimezoneOffset()
    },
    title: {
        text: null
    },
    tooltip: {
        enabled: false
    },
    credits: {
        enabled: false
    },
    legend: {
        enabled: false
    }
})
